import "../../assets/styles/components/textEditor.css";

import React, { useCallback, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { debounce } from "lodash";

import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";

import TextFormatter from "../editQuiz/TextFormatter";

const TextEditorEssayAnswer = ({
  placeholder,
  separateMenuBar = false,
  customClass,
  noFocusStyles = false,
  width = "100%",
  height = "100%",
  onContentChange,
  setEditor,
  initialContent = "",
  wordLimit = 2000,
  isTitle = false,
  customPlaceholder = null,
  minHeight = null,
  secure = false,
  highlightcopypaste = false,
  disablecopy = false,
}) => {
  const tiptapContainerRef = useRef(null);

  const customSuperscript = Superscript.extend({
    addKeyboardShortcuts() {
      return {
        "Mod-Shift-ArrowUp": () => this.editor.commands.toggleSuperscript(),
      };
    },
  });
  const customSubscript = Subscript.extend({
    addKeyboardShortcuts() {
      return {
        "Mod-Shift-ArrowDown": () => this.editor.commands.toggleSubscript(),
      };
    },
  });

  const extensions = [
    StarterKit.configure({
      bulletList: { keepMarks: true, keepAttributes: false },
      orderedList: { keepMarks: true, keepAttributes: false },
    }),
    Placeholder.configure({
      placeholder: placeholder ? placeholder : "Write something here...",
    }),
    Color.configure({ types: [TextStyle.name] }),
    TextAlign.configure({ types: ["heading", "paragraph", "codeBlock"] }),
    Highlight.configure(),
    customSuperscript.configure(),
    customSubscript.configure(),
    Underline.configure(),
  ];

  const sanitizeContent = (content) => {
    return DOMPurify.sanitize(content);
  };

  const convertHtmlToText = (html) => {
    const paragraphs = html.match(/<p[^>]*>(.*?)<\/p>/gi);
    if (paragraphs) {
      return paragraphs
        .map((p) => p.replace(/<[^>]+>/g, "").trim())
        .filter(Boolean)
        .join(" ");
    }
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.innerText || div.textContent || "").replace(/\s+/g, " ").trim();
  };

  const editor = useEditor({
    extensions: extensions,
    content: initialContent || "",
    editorProps: {
      attributes: {
        spellcheck: "false",
      },
      handleKeyDown: (view, event) => {
        if (
          (event.ctrlKey || event.metaKey) &&
          event.shiftKey &&
          event.key.toLowerCase() === "h"
        ) {
          event.preventDefault();
          return true;
        }
        if (isTitle && event.key === "Enter") {
          event.preventDefault();
          return true;
        }
        if (secure && (event.ctrlKey || event.metaKey) && event.key === "v") {
          event.preventDefault();
          return true;
        }
        return false;
      },
      handlePaste: (view, e) => {
        if (secure) {
          e.preventDefault();
          e.stopPropagation();
          return true;
        }

        const plainText = editor.getText();
        const pastedText = e.clipboardData.getData("text/plain");
        const plainTextWordCount = plainText
          .split(/\s+/)
          .filter(Boolean).length;
        const pastedTextWordCount = pastedText
          .split(/\s+/)
          .filter(Boolean).length;
        const totalWordCount = plainTextWordCount + pastedTextWordCount;

        if (totalWordCount >= wordLimit) {
          e.preventDefault();
          e.stopPropagation();
          const remainingWordCount = wordLimit - plainTextWordCount;
          const truncatedText = pastedText
            .split(/\s+/)
            .slice(0, remainingWordCount)
            .join(" ");
          if (highlightcopypaste && truncatedText) {
            editor.commands.insertContent(`<mark>${truncatedText}</mark>`);
            return true;
          }
          onContentChange(editor.getHTML(), totalWordCount);
          editor.commands.insertContent(truncatedText);
          return true;
        }
        if (highlightcopypaste) {
          e.preventDefault();
          e.stopPropagation();
          if (pastedText) {
            editor.commands.insertContent(
              `&nbsp;<mark>${pastedText}</mark>&nbsp;`,
            );
          }
          return true;
        }
        return false;
      },
    },
    onUpdate({ editor }) {
      const wordCount = editor.getText().split(/\s+/).filter(Boolean).length;
      if (wordCount > wordLimit) {
        return true;
      }
      onContentChange(editor.getHTML(), wordCount);
    },
  });

  const handleKeyDown = (e) => {
    const plainText = editor.getText();
    const wordCount = plainText.split(/\s+/).filter(Boolean).length;
    const { state } = editor;
    const { from } = state.selection;
    const textBefore = state.doc.textBetween(0, from, " ");
    const charBefore = textBefore.slice(-1);
    const isTypingAtWordBoundary = /\s/.test(charBefore);
    if (
      wordCount >= wordLimit &&
      e.key.length === 1 &&
      isTypingAtWordBoundary
    ) {
      e.preventDefault();
    }
  };

  const handleCopy = (e) => {
    if (disablecopy || secure) {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
  };

  useEffect(() => {
    if (!noFocusStyles && tiptapContainerRef.current) {
      tiptapContainerRef.current.classList.add("focusStyle");
      const tiptapEditor = tiptapContainerRef.current.querySelector(".tiptap");
      if (height) {
        tiptapEditor.style.height = height;
      }
      if (minHeight) {
        tiptapEditor.style.minHeight = minHeight;
      }
    }
    const handleContainerClick = () => {
      const html = editor.getHTML();
      if (isTitle && !html.includes("<h1")) {
        const content = editor.getText();
        editor.commands.setContent(
          `<h1 style="text-align: center">${content}</h1>`,
        );
        return;
      }
    };
    const editorElement = tiptapContainerRef.current.querySelector(".tiptap");
    editorElement.addEventListener("keydown", handleKeyDown);
    editorElement.addEventListener("copy", handleCopy);
    editorElement.addEventListener("cut", handleCopy);
    tiptapContainerRef.current.addEventListener("click", handleContainerClick);
    return () => {
      editorElement.removeEventListener("keydown", handleKeyDown);
      editorElement.removeEventListener("copy", handleCopy);
      editorElement.removeEventListener("cut", handleCopy);
    };
  }, [wordLimit, noFocusStyles, height, secure]);

  if (!editor) {
    return null;
  }

  return (
    <main style={{ width: width ? width : "100%" }}>
      <div
        ref={tiptapContainerRef}
        className={customClass}
        id="text-editor-container"
      >
        {separateMenuBar && <TextFormatter editor={editor} isEssayQuestion />}
        {!editor.getText() && customPlaceholder && (
          <div className="editor-custom-placeholder">{customPlaceholder}</div>
        )}
        <EditorContent
          className="EditorContent"
          onFocus={() => !separateMenuBar && setEditor(editor)}
          editor={editor}
        />
      </div>
    </main>
  );
};

export default TextEditorEssayAnswer;
